import React from 'react';
import { SelectBox } from 'components';

// Status 타입 정의
export interface Status {
  title: string;
  className: string;
}

// Status Badge Props
interface StatusBadgeProps {
  value: string;
  statusMap: Record<string, Status>;
  onClick?: () => void;
  className?: string;
}

// Status Badge 컴포넌트
export const StatusBadge: React.FC<StatusBadgeProps> = ({ 
  value, 
  statusMap, 
  onClick, 
  className = "" 
}) => {
  const status = statusMap[value] || statusMap['UNKNOWN'];
  
  return (
    <div
      title={value}
      onClick={onClick}
      className={onClick ? "cursor-pointer" : ""}
    >
      <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className} ${className}`}>
        <span className="text-nowrap text-small font-regular">{status.title}</span>
      </div>
    </div>
  );
};

// Editable Status Badge Props
interface EditableStatusBadgeProps {
  value: string;
  statusMap: Record<string, Status>;
  statusList: Array<{ value: number; label: string }>;
  isEditing: boolean;
  editingValue: string;
  onEditStart: () => void;
  onEditChange: (value: string) => void;
  onEditConfirm: () => void;
  statusValueToStatus: (value: number) => string;
  statusToStatusValue: (status: string) => number;
}

// Editable Status Badge 컴포넌트
export const EditableStatusBadge: React.FC<EditableStatusBadgeProps> = ({
  value,
  statusMap,
  statusList,
  isEditing,
  editingValue,
  onEditStart,
  onEditChange,
  onEditConfirm,
  statusValueToStatus,
  statusToStatusValue
}) => {
  if (isEditing) {
    return (
      <SelectBox
        className="min-w-[120px] z-[9999]"
        list={statusList as any}
        value={statusToStatusValue(editingValue)}
        setValue={(v) => onEditChange(statusValueToStatus(v))}
        label=""
        onChange={(newValue) => {
          const statusString = statusValueToStatus(newValue);
          onEditChange(statusString);
          onEditConfirm();
        }}
      />
    );
  }

  const status = statusMap[value] || statusMap['UNKNOWN'];
  
  return (
    <div
      title={value}
      onClick={onEditStart}
      style={{ cursor: 'pointer' }}
    >
      <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className}`}>
        <span className="text-nowrap text-small font-regular">{status.title}</span>
      </div>
    </div>
  );
};

// Cancel Button Props
interface CancelButtonProps {
  cancelable: boolean;
  onClick?: () => void;
  className?: string;
}

// Cancel Button 컴포넌트
export const CancelButton: React.FC<CancelButtonProps> = ({ 
  cancelable, 
  onClick, 
  className = "" 
}) => {
  if (cancelable) {
    return (
      <button
        type="button"
        className={`cursor-pointer flex flex-row gap-1 items-center rounded border px-2 h-6 text-xs font-regular bg-white text-black border-border_primary hover:bg-blue-50 ${className}`}
        onClick={onClick}
      >
        <span className="select-none text-nowrap">Cancel</span>
      </button>
    );
  } else {
    return (
      <button
        type="button"
        className={`cursor-not-allowed flex flex-row gap-1 items-center rounded border px-2 h-6 text-xs font-regular bg-gray-100 text-gray-400 border-border_primary opacity-50 ${className}`}
        disabled
      >
        <span className="select-none text-nowrap">Cancel</span>
      </button>
    );
  }
}; 